import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PartnersCarousel from "@/components/PartnersCarousel";
import { useEffect } from "react";
import { Shield, Users, Award, Target, Linkedin } from "lucide-react";
import SEO from "@/components/SEO";
import { buildBreadcrumbs } from "@/lib/structured-data";

export default function AboutPage() {
  
  // Scroll animation effect
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate');
        }
      });
    }, observerOptions);

    // Observe all elements with scroll-animate class
    const animateElements = document.querySelectorAll('.scroll-animate');
    animateElements.forEach((el) => observer.observe(el));

    return () => {
      animateElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  return (
    <div className="min-h-screen pt-28">
      <SEO
        title="About Us - 20+ Years Property Search Experience"
        description="Learn about Whitefield Legal Services - over 20 years of trusted property search expertise serving conveyancers across the UK."
        keywords="about whitefield legal, property search company, conveyancing search provider, UK property searches"
        canonicalUrl="/about"
        structuredData={buildBreadcrumbs([{ name: "Home", path: "/" }, { name: "About" }])}
      />
      <Navbar />

      {/* Hero Section with Video Backdrop */}
      <section className="relative py-32 bg-wl-navy overflow-hidden">
        {/* Video Background */}
        <video 
          autoPlay 
          loop 
          muted 
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="/about-back.mp4" type="video/mp4" />
          {/* Fallback for browsers that don't support video */}
          <div 
            className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: 'url(/city-map.jpg)' }}
          ></div>
        </video>
        
        {/* Single overlay for darkening */}
        <div className="absolute inset-0 bg-wl-navy/80"></div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 md:p-8 border border-white/20">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              About Us
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
              We are the trusted property search experts serving the conveyancing industry for over 20 years with unmatched relationships and compliance standards.
            </p>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="pt-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 scroll-animate">
            <h2 className="text-3xl md:text-4xl font-bold text-wl-navy mb-6">Our Story</h2>
            <p className="text-xl text-wl-gray max-w-3xl mx-auto">
              Built on the belief that personal service and professional excellence should go hand in hand, Whitefield Legal Services has earned the trust of conveyancing professionals across the UK. As an independent provider, we offer a comprehensive range of property searches tailored to the unique needs of every transaction.
            </p>
          </div>
        </div>
      </section>

      {/* Our Team Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 scroll-animate">
            <h2 className="text-3xl md:text-4xl font-bold text-wl-navy mb-6">Our Team</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            {/* Bharti Bhuva */}
            <div className="bg-white p-8 rounded-2xl shadow-lg scroll-animate flex flex-col h-full">
              <div className="flex-grow">
                <img src="/bharti-headshot.jpeg" alt="Bharti Bhuva" className="w-36 h-36 rounded-full mx-auto mb-6 ring-4 ring-wl-blue/20" />
                <h3 className="text-2xl font-bold text-wl-navy text-center mb-2">Bharti Bhuva</h3>
                <p className="text-center text-wl-green font-semibold mb-4">Founder & Director</p>
                <p className="text-wl-gray mb-6 text-left">
                  Bharti’s journey began in local government, where she identified key inefficiencies in property search processing. Driven by a vision for a more streamlined and client focused service, she founded Whitefield Legal Services. Since its inception, Bharti has championed faster turnarounds, precision, and a personal touch, all while upholding the highest quality standards.
                </p>
                <p className="text-wl-gray mb-6 text-left">
                  She has successfully navigated the dynamic property market, cultivated a dedicated team, and maintained the firm’s independence against franchise offers. Her unwavering commitment to agility and client responsiveness has been central to Whitefield Legal’s enduring success and growth in a competitive industry.
                </p>
              </div>
              <a href="https://www.linkedin.com/in/bharti-bhuva-48339018/" target="_blank" rel="noopener noreferrer" className="mt-auto mx-auto text-wl-blue hover:text-wl-navy transition-colors flex items-center gap-2">
                <Linkedin className="w-5 h-5" />
                <span>Connect on LinkedIn</span>
              </a>
            </div>
            
            {/* Lara Waelte */}
            <div className="bg-white p-8 rounded-2xl shadow-lg scroll-animate flex flex-col h-full">
              <div className="flex-grow">
                <img src="/lara-headshot.jpeg" alt="Lara Waelte" className="w-36 h-36 rounded-full mx-auto mb-6 ring-4 ring-wl-blue/20" />
                <h3 className="text-2xl font-bold text-wl-navy text-center mb-2">Lara Waelte</h3>
                <p className="text-center text-wl-green font-semibold mb-4">Operations &amp; Business Support Partner</p>
                <p className="text-wl-gray mb-6 text-left">
                  Every thriving business relies on seamless operational mechanics behind the scenes, and that is exactly what Lara brings to Whitefield Legal Services. With a strong background in project management and years of dedicated experience helping companies refine their processes, Lara and her team at Top Tier Business Support ensure WLS operates at peak efficiency.
                </p>
                <p className="text-wl-gray mb-6 text-left">
                  From bookkeeping and day-to-day operations to refining client workflows and managing the company's online presence and social media, Lara oversees everything required to keep the business running smoothly.
                </p>
                <p className="text-wl-gray mb-6 text-left">
                  While Whitefield Legal benefits from the collective backing of the Top Tier team, Lara still chooses to support Bharti personally because she deeply admires Bharti's passion, her genuine care for her clients, and her approach to business.
                </p>
              </div>
              <a href="https://www.linkedin.com/in/lara-waelte-top-tier" target="_blank" rel="noopener noreferrer" className="mt-auto mx-auto text-wl-blue hover:text-wl-navy transition-colors flex items-center gap-2">
                <Linkedin className="w-5 h-5" />
                <span>Connect on LinkedIn</span>
              </a>
            </div>

            {/* Wider team – compact cards using the same headshot style as above */}
            {/* One flex-wrap row per line (not grid) so short rows stay centred:
                line 1 = managers + digital support, line 2 = admin support */}
            {[
              [
                { name: 'Vikas Gupta', role: 'Client & IT Manager', img: '/vikas-headshot.jpg' },
                { name: 'Renu Sadani', role: 'Operations Manager', img: '/renu-headshot.jpg' },
                { name: 'Irene Marquez Rivero', role: 'Digital & Creative Support', img: '/irene-headshot.jpg' },
              ],
              [
                { name: 'Mansi Parmar', role: 'Admin Support', img: '/mansi-headshot.jpg' },
                { name: 'Tanisha Chauhan', role: 'Admin Support', img: '/tanisha-headshot.jpg' },
              ],
            ].map((row, i) => (
              <div key={i} className="md:col-span-2 flex flex-wrap justify-center gap-8">
                {row.map((member) => (
                  <div key={member.name} className="w-full sm:w-[calc(50%-1rem)] lg:w-[calc(25%-1.5rem)] bg-white p-8 rounded-2xl shadow-lg scroll-animate flex flex-col items-center">
                    <img src={member.img} alt={member.name} loading="lazy" className="w-36 h-36 rounded-full object-cover mb-6 ring-4 ring-wl-blue/20" />
                    <h3 className="text-xl font-bold text-wl-navy text-center mb-2">{member.name}</h3>
                    <p className="text-center text-wl-green font-semibold">{member.role}</p>
                  </div>
                ))}
              </div>
            ))}

            {/* Award Card */}
            <div className="md:col-span-2 mt-8 bg-white p-8 rounded-2xl shadow-lg scroll-animate flex flex-col md:flex-row items-center gap-8 border-t-4 border-yellow-400">
              <img src="/sme-herts-finalist.jpg" alt="SME Hertfordshire Business Awards 2025 Finalist" className="w-48 h-auto rounded-lg" />
              <div className="text-left">
                <h3 className="text-2xl font-bold text-wl-navy mb-3">Recognised for Excellence</h3>
                <p className="text-wl-gray">
                  Bharti has been recognised for her leadership and service excellence, and was proudly shortlisted for <strong>Business Person of the Year</strong> at the Watford Chamber of Conscience SME Hertfordshire Business Awards 2025 – a testament to her resilience, integrity, and entrepreneurial spirit.
                </p>
              </div>
            </div>

            {/* LEAP Modern Law Award Card */}
            <div className="md:col-span-2 mt-8 bg-white p-8 rounded-2xl shadow-lg scroll-animate flex flex-col md:flex-row items-center gap-8 border-t-4 border-green-500">
              <img src="/search-provider-2024.jpg" alt="LEAP Modern Law Conveyancing Awards 2024 - Search Provider of the Year Shortlisted" className="w-48 h-auto rounded-lg" />
              <div className="text-left">
                <h3 className="text-2xl font-bold text-wl-navy mb-3">Industry Recognition</h3>
                <p className="text-wl-gray">
                  We are proud to have been shortlisted for <strong>Search Provider of the Year: 2024</strong> in the LEAP Modern Law Conveyancing Awards – recognition of our commitment to excellence in property search services and our trusted relationships with conveyancing professionals across the UK.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 scroll-animate">
            <h2 className="text-3xl md:text-4xl font-bold text-wl-navy mb-6">Our Expertise</h2>
            <p className="text-xl text-wl-gray max-w-3xl mx-auto">
              Our experienced team combines deep industry knowledge with cutting-edge technology 
              to deliver exceptional service every time.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl border-l-4 border-wl-blue shadow-sm scroll-animate">
              <Target className="w-12 h-12 text-wl-blue mb-4" />
              <h3 className="text-xl font-bold text-wl-navy mb-3">Industry Experience</h3>
              <p className="text-wl-gray">
                Over 10 years direct experience in Land Charges at Council level, 
                providing unparalleled insight into the search process.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-2xl border-l-4 border-wl-green shadow-sm scroll-animate">
              <Users className="w-12 h-12 text-wl-green mb-4" />
              <h3 className="text-xl font-bold text-wl-navy mb-3">Personal Service</h3>
              <p className="text-wl-gray">
                You're never just a number with us. Our dedicated team provides 
                personal attention and builds lasting professional relationships.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-2xl border-l-4 border-wl-navy shadow-sm scroll-animate">
              <Award className="w-12 h-12 text-wl-navy mb-4" />
              <h3 className="text-xl font-bold text-wl-navy mb-3">Professional Standards</h3>
              <p className="text-wl-gray">
                Continuous professional development, training, and adherence to 
                the highest industry standards and best practices.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance & Certifications */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 scroll-animate">
            <h2 className="text-3xl md:text-4xl font-bold text-wl-navy mb-6">Compliance & Professional Standards</h2>
            <p className="text-xl text-wl-gray max-w-3xl mx-auto">
              We maintain the highest professional standards through comprehensive insurance, 
              regulatory compliance, and continuous professional development.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-wl-light p-8 rounded-2xl scroll-animate">
              <Shield className="w-12 h-12 text-wl-green mb-4" />
              <h3 className="text-2xl font-bold text-wl-navy mb-4">Insurance & Protection</h3>
              <ul className="space-y-3 text-wl-gray">
                <li>• £2 million professional indemnity insurance</li>
                <li>• Comprehensive public liability coverage</li>
                <li>• Regular policy reviews and updates</li>
                <li>• Full claims handling support</li>
              </ul>
            </div>
            
            <div className="bg-wl-light p-8 rounded-2xl scroll-animate">
              <Award className="w-12 h-12 text-wl-blue mb-4" />
              <h3 className="text-2xl font-bold text-wl-navy mb-4">Professional Memberships</h3>
              <ul className="space-y-3 text-wl-gray">
                <li>• IPSA (Independent Property Search Association) - 15 years</li>
                <li>• PCCB (Property Codes Compliance Board) registered</li>
                <li>• TPO (The Property Ombudsman) scheme member</li>
                <li>• Continuous professional development programs</li>
              </ul>
            </div>
          </div>
          
          {/* Certification Logos */}
          <div className="mt-16 text-center">
            <h4 className="text-lg font-semibold text-wl-navy mb-8">Trusted & Certified by Industry Leaders</h4>
            <div className="flex justify-center items-center space-x-12 opacity-80">
              <img src="/ipsa-final-logo.png" alt="IPSA Member" className="h-16 w-auto" />
              <img src="/tpo-logo.jpg" alt="TPO Scheme" className="h-16 w-auto" />
              <img src="/search-code-logo.gif" alt="Search Code" className="h-16 w-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <PartnersCarousel />

      <Footer />
    </div>
  );
} 